import { createAsyncThunk } from '@reduxjs/toolkit';

import { partnerAPI } from '@/modules/partner/api';
import { IPartner } from '@/modules/partner/model';
import { PayoutsInfoCompanyForm, PayoutsInfoForm } from '@/modules/partner/model/payoutsInfoForm';
import { namespace } from '@/modules/partner/namespace';
import { getPartner } from '@/modules/partner/store/selectors';
import { isTaxProfileRequiresVat } from '@/modules/taxProfile/model/taxProfile';
import type { RootState } from '@/root-store';
import { ThunkResult } from '@/shared/actions/thunk-result';
import { ISubmitFormResult } from '@/shared/models/formSubmit';

import { updatePartner } from '../actions';

let lastRequestId: string | undefined;

type FormValues = (PayoutsInfoForm | PayoutsInfoCompanyForm) & { isCompany?: boolean };

const isCompanyValues = (formValues: FormValues): formValues is PayoutsInfoCompanyForm =>
  Boolean(formValues.isCompany);

const updatePayoutsInfo = createAsyncThunk<
  ThunkResult<ISubmitFormResult<PayoutsInfoForm | PayoutsInfoCompanyForm>>,
  FormValues
>(`${namespace}/update-payouts-info`, async (formValues, { dispatch, requestId, getState }) => {
  try {
    if (lastRequestId !== undefined) {
      return { isAlreadyExecuted: true };
    }

    lastRequestId = requestId;

    const partner = getPartner(getState() as RootState);

    const isCompanyForm = isCompanyValues(formValues);
    const vatRegNo = isTaxProfileRequiresVat(formValues.taxProfile) ? formValues.vatRegNo : '';
    let payload: IPartner = {} as any;
    let payloadState = {};

    if (isCompanyForm) {
      const legalRegisteredAddress = {
        ...partner.legalRegisteredAddress,
        line1: formValues.legalAddress1,
      };

      payload = {
        ...partner,
        taxProfile: formValues.taxProfile,
        vatRegNo,
        legalName: formValues.legalName,
        companyRegNumber: formValues.companyRegNumber,
        legalRegisteredAddress,
      };
      payloadState = {
        taxProfile: formValues.taxProfile,
        vatRegNo,
        legalName: formValues.legalName,
        companyRegNumber: formValues.companyRegNumber,
        legalAddress1: formValues.legalAddress1,
        legalRegisteredAddress,
      };
    } else {
      payload = {
        ...partner,
        taxProfile: formValues.taxProfile,
        vatRegNo,
      };
      payloadState = {
        taxProfile: formValues.taxProfile,
        vatRegNo,
      };
    }

    const result = await partnerAPI.updatePaymentInfo(payload);

    if (result.baseErrors) {
      return {
        isSucceed: false,
        result: { baseErrors: result.baseErrors },
      };
    }

    dispatch(updatePartner(payloadState));

    return {
      isSucceed: true,
    };
  } finally {
    lastRequestId = undefined;
  }
});

export { updatePayoutsInfo };
